// Tiempo de espera de la tienda online (PR K, spec 2026-10-19 §6).
//
// Puro y sin servidor: recibe filas de `orders` y un `now`, devuelve lo que se le
// promete al cliente. Lo alimenta `waitEstimateServer.ts` (una consulta + caché
// de 90 s) y lo pintan la barra, el hero, el carrito, la confirmación y el
// seguimiento. El MISMO objeto viaja a todas esas superficies para que nunca
// digan dos números distintos.
//
// Con `features.live_wait_time` apagado todo usa `fixedWait(pickup_wait_minutes)`
// y `waitText` llama exactamente las claves de i18n de antes: la tienda queda
// byte a byte como en `main`.
//
// Inicio = `orders.created_at` (no existe "entró a cocina"; el pago llega ~0,1 min
// después). Fin = `orders.ready_at`, que estampa el K10. NO se usa
// `order_station_tickets.ready_at`: antes del 2026-10-18 la vista general copiaba
// su hora a todas las estaciones.

import { percentile } from "@/lib/kitchen/kitchenMetrics"

export interface WaitOrderRow {
  created_at: string
  ready_at: string | null
  status: string | null
  scheduled_for: string | null
}

export interface WaitEstimate {
  mode: "fixed" | "live"
  /** Número único: anticipación del primer horario, hora estimada del seguimiento y lo que se suma al repartidor. */
  minutes: number
  /** Rango que se muestra (solo en "live"; en "fixed" low = high = minutes y no se usa para texto). */
  low: number
  high: number
  highDemand: boolean
  /** Diagnóstico: órdenes en la ventana y abiertas en cocina (0 en "fixed"). */
  sample: number
  queue: number
}

/** Punto de partida de la spec; se calibran mirando EF con la bandera prendida. */
export const LIVE_WAIT = {
  windowMin: 40,
  minSample: 4,
  percentile: 75,
  freeQueue: 3,
  perQueuedMin: 1.5,
  capMin: 60,
  minDurMin: 1,
  maxDurMin: 120,
  rangeStep: 5,
  rangeWidth: 10,
} as const

/** Lo que la tienda muestra hoy: `pickup_wait_minutes`, o 20 si no hay. */
export function fixedWait(pickupWaitMinutes: number | null | undefined): WaitEstimate {
  const minutes = pickupWaitMinutes ?? 20
  return { mode: "fixed", minutes, low: minutes, high: minutes, highDemand: false, sample: 0, queue: 0 }
}

/**
 * Dónde muestra la tienda el tiempo real (`features.live_wait_time`):
 *  - `"confirmation"`: solo después de pagar (confirmación + seguimiento del SMS);
 *    el menú y el checkout siguen con el fijo.
 *  - `true` o `"everywhere"`: en todos lados (hero, barra, carrito, ¿Cuándo?,
 *    primer horario, confirmación y seguimiento).
 *  - cualquier otro valor: apagado.
 */
export type LiveWaitMode = "off" | "confirmation" | "everywhere"

export function liveWaitMode(features: unknown): LiveWaitMode {
  if (!features || typeof features !== "object") return "off"
  const v = (features as Record<string, unknown>).live_wait_time
  if (v === true || v === "everywhere") return "everywhere"
  if (v === "confirmation") return "confirmation"
  return "off"
}

/** Qué valor ve el cliente antes de pagar (`store`) y en la confirmación (`confirm`). */
export function waitForSurfaces(
  mode: LiveWaitMode,
  live: WaitEstimate,
  fixed: WaitEstimate,
): { store: WaitEstimate; confirm: WaitEstimate } {
  if (mode === "everywhere") return { store: live, confirm: live }
  if (mode === "confirmation") return { store: fixed, confirm: live }
  return { store: fixed, confirm: fixed }
}

/** Hay que calcular el tiempo real (en cualquiera de los dos modos). */
export function isLiveWaitEnabled(features: unknown): boolean {
  return liveWaitMode(features) !== "off"
}

const OPEN_IN_KITCHEN = new Set(["confirmed", "in_progress"])
const NOT_SERVED = new Set(["voided", "cancelled"])

function liveFrom(estimate: number, sample: number, queue: number, highDemandIn = false): WaitEstimate {
  const highDemand = highDemandIn || estimate > LIVE_WAIT.capMin
  const capped = highDemandIn ? estimate : Math.min(estimate, LIVE_WAIT.capMin)
  const minutes = Math.ceil(capped)
  const low = Math.floor(capped / LIVE_WAIT.rangeStep) * LIVE_WAIT.rangeStep
  return { mode: "live", minutes, low, high: low + LIVE_WAIT.rangeWidth, highDemand, sample, queue }
}

export function computeLiveWait(rows: WaitOrderRow[], fixedMinutes: number, now: Date): WaitEstimate {
  const nowMs = now.getTime()
  const windowFrom = nowMs - LIVE_WAIT.windowMin * 60_000
  const openFrom = nowMs - LIVE_WAIT.maxDurMin * 60_000

  const durations: number[] = []
  let queue = 0
  for (const r of rows) {
    if (r.scheduled_for) continue
    const created = Date.parse(r.created_at)
    if (created > nowMs) continue
    const ready = r.ready_at ? Date.parse(r.ready_at) : null
    // Abierta en `now`: sin marca de lista, o marcada DESPUÉS de `now` (solo pasa
    // al calcular "a la hora de un pedido" para el seguimiento).
    const openNow =
      ready == null ? !!r.status && OPEN_IN_KITCHEN.has(r.status) : ready > nowMs && !(r.status && NOT_SERVED.has(r.status))
    if (openNow) {
      if (created >= openFrom) queue++
      continue
    }
    if (ready == null || (r.status && NOT_SERVED.has(r.status))) continue
    if (ready < windowFrom) continue
    const dur = (ready - created) / 60_000
    if (dur < LIVE_WAIT.minDurMin || dur > LIVE_WAIT.maxDurMin) continue
    durations.push(dur)
  }

  const base =
    durations.length >= LIVE_WAIT.minSample
      ? percentile([...durations].sort((a, b) => a - b), LIVE_WAIT.percentile)
      : fixedMinutes
  const adjust = Math.max(0, queue - LIVE_WAIT.freeQueue) * LIVE_WAIT.perQueuedMin
  return liveFrom(Math.max(fixedMinutes, base + adjust), durations.length, queue)
}

/** Domicilio = cocina + ETA del repartidor de la cotización. */
export function withCourier(est: WaitEstimate, courierMinutes: number | null): WaitEstimate {
  if (courierMinutes == null) return est
  if (est.mode === "fixed") {
    const minutes = est.minutes + courierMinutes
    return { ...est, minutes, low: minutes, high: minutes }
  }
  return liveFrom(est.minutes + courierMinutes, est.sample, est.queue, est.highDemand)
}

export type WaitT = (key: string, values?: Record<string, number>) => string
export type WaitTextKind = "readyIn" | "pickupMeta" | "deliveryMeta" | "approx"

const FIXED_KEY: Record<WaitTextKind, string> = {
  readyIn: "readyIn",
  pickupMeta: "modePickupMeta",
  deliveryMeta: "modeDeliveryEta",
  approx: "minutesApprox",
}

/** `t` viene del componente con su namespace (`public.microsite` para `readyIn`, `public.onlineOrder.checkout` para el resto). */
export function waitText(est: WaitEstimate, kind: WaitTextKind, t: WaitT): string {
  if (est.mode === "fixed") return t(FIXED_KEY[kind], { minutes: est.minutes })
  if (est.highDemand) return t("waitHighDemand")
  return t(`${FIXED_KEY[kind]}Range`, { low: est.low, high: est.high })
}
